import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight } from "lucide-react";

const PRICE_PER = 100;

const emptyForm = {
  school_name: "",
  school_address: { street: "", city: "", state: "", zip: "" },
  district: "",
  admin_name: "",
  admin_email: "",
  billing_name: "",
  billing_email: "",
  accounts_requested: 5,
  po_number: "",
  notes: "",
};

const PurchaseOrderRequest = () => {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const setAddr = (k, v) => setForm({ ...form, school_address: { ...form.school_address, [k]: v } });
  const total = (parseInt(form.accounts_requested) || 0) * PRICE_PER;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.school_name.trim() || !form.admin_name.trim() || !form.admin_email.trim() || !form.school_address.street.trim()) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!form.accounts_requested || form.accounts_requested < 1) {
      toast.error("Please request at least one teacher account.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/purchase-orders", { ...form, accounts_requested: parseInt(form.accounts_requested) });
      setSubmitted(r.data.purchase_order);
      toast.success("Purchase order request submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-mm-parchment bg-topo">
        <div className="container mx-auto py-10 max-w-2xl">
          <div className="flex justify-center"><Logo size={48} /></div>
          <div className="mt-8 bg-white border-2 border-mm-gold/40 rounded-2xl p-8 text-center" data-testid="po-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-mm-gold/20 flex items-center justify-center">
              <ShieldCheck size={32} strokeWidth={2.5} className="text-mm-navy" />
            </div>
            <h1 className="font-heading text-3xl font-black text-mm-navy mt-4">Request received — thank you!</h1>
            <p className="text-mm-navy/80 mt-3">
              We'll send your official invoice to <b>{submitted.billing_email || submitted.admin_email}</b> within 1 business day.
              Once payment is confirmed we'll activate {submitted.accounts_requested} teacher accounts within 24 hours.
            </p>
            <p className="text-sm text-mm-navy/60 mt-4">Questions? <a href="mailto:theswoom@gmail.com" className="font-bold text-mm-navy underline">theswoom@gmail.com</a></p>
            <Link to="/pricing" className="btn-trail btn-outline-navy text-sm mt-6 inline-flex">Back to pricing</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <div className="container mx-auto py-10 max-w-2xl">
        <div className="flex justify-center"><Logo size={48} /></div>
        <div className="mt-6 text-center">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">For schools & districts</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy mt-2" data-testid="po-page-title">Purchase Order Request</h1>
          <p className="text-mm-navy/70 mt-3 max-w-xl mx-auto text-sm">
            Mind Map makes it easy for schools to purchase teacher accounts using a purchase order. Complete the form below and we will send you an invoice within 1 business day. Accounts are activated as soon as payment is confirmed.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 bg-white border-2 border-mm-navy/10 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="po-form">
          <Section title="School information">
            <Field label="School name *" value={form.school_name} onChange={(v) => setForm({ ...form, school_name: v })} testid="po-school-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="District" value={form.district} onChange={(v) => setForm({ ...form, district: v })} testid="po-district" />
              <div />
            </div>
            <Field label="Street address *" value={form.school_address.street} onChange={(v) => setAddr("street", v)} testid="po-street" />
            <div className="grid grid-cols-3 gap-3">
              <Field label="City" value={form.school_address.city} onChange={(v) => setAddr("city", v)} testid="po-city" />
              <Field label="State" value={form.school_address.state} onChange={(v) => setAddr("state", v)} testid="po-state" />
              <Field label="ZIP" value={form.school_address.zip} onChange={(v) => setAddr("zip", v)} testid="po-zip" />
            </div>
          </Section>

          <Section title="Administrator contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name *" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} testid="po-admin-name" />
              <Field label="Email *" type="email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testid="po-admin-email" />
            </div>
          </Section>

          <Section title="Billing contact (optional if same as administrator)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Name" value={form.billing_name} onChange={(v) => setForm({ ...form, billing_name: v })} testid="po-billing-name" />
              <Field label="Email" type="email" value={form.billing_email} onChange={(v) => setForm({ ...form, billing_email: v })} testid="po-billing-email" />
            </div>
          </Section>

          <Section title="Order">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Number of teacher accounts *" type="number" min="1" max="500" value={form.accounts_requested} onChange={(v) => setForm({ ...form, accounts_requested: v })} testid="po-accounts" />
              <Field label="Purchase order number (optional)" value={form.po_number} onChange={(v) => setForm({ ...form, po_number: v })} testid="po-number" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Additional notes or special requests</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3} maxLength={2000}
                className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel"
                data-testid="po-notes" />
            </div>
          </Section>

          {/* Pricing summary */}
          <div className="rounded-xl bg-mm-parchment border-2 border-mm-navy/10 p-5" data-testid="po-pricing-summary">
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Pricing summary</div>
            <div className="mt-2 flex items-baseline justify-between">
              <div className="text-mm-navy text-sm">
                Teacher Platform accounts: <b>{form.accounts_requested || 0}</b> × $100.00
              </div>
              <div className="font-heading text-2xl font-black text-mm-navy" data-testid="po-total">
                ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-trail btn-gold w-full disabled:opacity-50" data-testid="po-submit-btn">
            {busy ? "Submitting..." : "Submit Request"} <ArrowRight size={14} strokeWidth={2.5} />
          </button>

          <p className="text-center text-xs text-mm-navy/60">
            Questions? Contact us at <a href="mailto:theswoom@gmail.com" className="font-bold text-mm-navy underline">theswoom@gmail.com</a> — we typically respond within 1 business day.
          </p>
        </form>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">{title}</div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, testid, type = "text", min, max }) => (
  <div>
    <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">{label}</label>
    <input
      type={type}
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel"
      data-testid={testid}
    />
  </div>
);

export default PurchaseOrderRequest;
